import React, { MouseEvent, useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import htmlParser from 'html-react-parser';
import AnimateHeight from 'react-animate-height';
import { Transition, CSSTransition } from 'react-transition-group';
import { Constants } from '@praktio/common';

import CATAChoiceExplanationPanel from './CATAChoiceExplanationPanel';
import { cleanContent } from '../../utils/slide';

// Ducks
import { update as updateQuiz } from '../../ducks/quizLitmosReducer';
import { RootState } from '../../ducks/configureStore';

// Styles
import './CATAChoice.scss';
import { SupplementalDocStates, WindowSizes } from '../../utils/constants';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { TemplateTypes } from '@praktio/common/lib/constants';

const { CataTypes } = Constants;
const bestEditTypes = [CataTypes.EditHorizontal, CataTypes.EditVertical];

interface NudgeBannerContentIProps {
  correct?: boolean;
  hasInteractiveDocument?: boolean;
  windowSize: string;
  layoutIsHorizontal: boolean;
  slideSubType: string;
  slideMainType: string;
}

const NudgeBannerContent = ({
  correct,
  hasInteractiveDocument,
  windowSize,
  layoutIsHorizontal,
  slideMainType,
  slideSubType,
}: NudgeBannerContentIProps) => {
  const review = 'Review';
  const reviewThis = review + ' this';
  const answer = 'answer';
  const longTextPre = reviewThis;
  const longTextPost = `answer, which you ${correct ? `didn't select` : `selected`}`;
  const mediumTextPre = reviewThis;
  const mediumTextPost = answer;
  const shortTextPre = review;
  const shortTextPost = answer;
  const isCATAVertical =
    slideMainType === TemplateTypes.CheckAllApply && slideSubType === CataTypes.EditVertical;
  const isCATAHorizontal =
    slideMainType === TemplateTypes.CheckAllApply && slideSubType === CataTypes.EditHorizontal;

  const isStandardCATAwDoc =
    slideMainType === TemplateTypes.CheckAllApply &&
    (!slideSubType ||
      (slideSubType &&
        [CataTypes.Standard, CataTypes.Comment, CataTypes.Provision].includes(
          slideSubType.toUpperCase(),
        ))) &&
    hasInteractiveDocument;

  const preText = () => {
    if (isCATAVertical || isStandardCATAwDoc) {
      if (windowSize === WindowSizes.Small) {
        return shortTextPre;
      }
      return mediumTextPre;
    }
    if (isCATAHorizontal) {
      return mediumTextPre;
    }
    return longTextPre;
  };
  const postText = () => {
    if (isCATAVertical || isStandardCATAwDoc) {
      if (windowSize === WindowSizes.Small) {
        return shortTextPost;
      }
      return mediumTextPost;
    }
    if (isCATAHorizontal) {
      return mediumTextPost;
    }
    return longTextPost;
  };
  return (
    <>
      {preText()}
      <span className={correct ? 'correct' : 'incorrect'}>{correct ? 'correct' : 'incorrect'}</span>
      {postText()}
    </>
  );
};
interface CATAChoiceNudgeBannerIProps {
  hasInteractiveDocument?: boolean;
  layoutIsHorizontal?: boolean;
  isSelected: boolean;
  correct: boolean;
  windowSize: string;
  slideMainType: string;
  slideSubType: string;
}
const CATAChoiceNudgeBanner = ({
  hasInteractiveDocument,
  layoutIsHorizontal,
  isSelected,
  correct,
  windowSize,
  slideMainType,
  slideSubType,
}: CATAChoiceNudgeBannerIProps) => {
  if ((isSelected && !correct) || (!isSelected && correct)) {
    return (
      <div
        className={classNames('CATAChoice-nudge-banner', {
          'full-width': hasInteractiveDocument,
          'horizontal-layout': layoutIsHorizontal,
        })}
      >
        <NudgeBannerContent
          correct={correct}
          hasInteractiveDocument={hasInteractiveDocument}
          windowSize={windowSize}
          layoutIsHorizontal={!!layoutIsHorizontal}
          slideMainType={slideMainType}
          slideSubType={slideSubType}
        />
        <div className="CATAChoice-nudge-banner-arrow"></div>
      </div>
    );
  }
  return null;
};

// TODO - You selected this on a prior try/you didn't select this on a prior try.
interface IProps {
  choiceClickFunction: (
    event: MouseEvent,
    setEyeIconVisibility: Dispatch<SetStateAction<boolean>>,
  ) => void;
  explanationClickFunction: (event: MouseEvent, id: string) => void;
  eyeClickFunction: (event: MouseEvent, id: string, choiceDisabled?: boolean) => void;
  caretClickFunction: (choiceId: string) => void;
  activityComplete: boolean;
  hasInteractiveDocument: boolean;
  isBestEdit: boolean;
  layoutIsHorizontal: boolean;
  choice: Annotation;
  arrayOfSelectedChoices: string[];
  slideMainType: string;
  slideSubType: string;
  supplementalDocState?: SupplementalDocStates;
  editSetPreview: string;
  caretExpanded: boolean;
  hasAnswerBase: boolean;
  explanationExpanded?: boolean;
  windowSize: string;
  hidePreviewCaret?: boolean;
}

interface IMouseEnterProps {
  e: MouseEvent<HTMLButtonElement>;
  choiceDisabled: boolean;
  isFeedbackButton: boolean;
}

const CATAChoice = ({
  choiceClickFunction,
  explanationClickFunction,
  eyeClickFunction,
  caretClickFunction,
  activityComplete,
  hasInteractiveDocument,
  isBestEdit,
  layoutIsHorizontal,
  choice,
  arrayOfSelectedChoices,
  slideMainType,
  slideSubType,
  supplementalDocState,
  editSetPreview,
  caretExpanded,
  hasAnswerBase,
  explanationExpanded,
  windowSize,
  hidePreviewCaret,
}: IProps) => {
  const dispatch = useDispatch();

  // Redux State
  const {
    choicesShouldPulse,
    explanationButtonsShouldPulse,
    tooltipEngaged,
    displayedEditId,
    isSubmitting,
  } = useSelector((state: RootState) => {
    const { quizLitmos, learner } = state;
    const { choicesShouldPulse, explanationButtonsShouldPulse, tooltipEngaged, displayedEditId } =
      quizLitmos;

    const { isSubmitting } = learner || {};
    return {
      choicesShouldPulse,
      explanationButtonsShouldPulse,
      tooltipEngaged,
      displayedEditId,
      isSubmitting,
    };
  });

  // Refs
  const prevTooltipEngaged = useRef(tooltipEngaged);

  const choiceId = choice._id || '';
  const choiceText = choice.name || '';
  const isSelected = !!(choice._id && arrayOfSelectedChoices.indexOf(choice._id) > -1);
  const explanation = choice.text || '';
  const correct = !!choice.correct;

  // Local State
  const [showEyeIcon, setShowEyeIcon] = useState(displayedEditId === choiceId);

  // Hide eye icon if it was selected, but a different choice is made.
  useEffect(() => {
    if (displayedEditId !== choiceId) {
      setShowEyeIcon(false);
      return;
    }

    setShowEyeIcon(true);
  }, [displayedEditId, choiceId]);

  // Turn off purple pulsing if the tooltip was engaged but user has taken
  // action to disengage.
  useEffect(() => {
    if (prevTooltipEngaged.current && !tooltipEngaged) {
      dispatch(
        updateQuiz({
          choicesShouldPulse: false,
          explanationButtonsShouldPulse: false,
        }),
      );
    }

    prevTooltipEngaged.current = tooltipEngaged;
  }, [tooltipEngaged, dispatch]);

  const explanationButton = () => {
    return (
      <button
        className={classNames('Button CATAChoice-show-explanation-button', {
          engaged: explanationExpanded,
          'should-pulse': explanationButtonsShouldPulse,
        })}
        onMouseEnter={(e) =>
          mouseEnter({ e, choiceDisabled: !!choiceDisabled, isFeedbackButton: true })
        }
        onMouseLeave={mouseLeave}
        onClick={(e) => {
          if (!e) return;
          explanationClickFunction(e, choiceId);
        }}
      >
        ?
      </button>
    );
  };

  const feedbackBadge = () => {
    let iconName = '';
    let className = '';
    if (isSelected && correct) {
      iconName = 'done';
      className = 'correct';
    }
    if ((isSelected && !correct) || (!isSelected && correct)) {
      iconName = 'close';
      className = 'incorrect';
    }

    return (
      <div
        className={classNames('CATAChoice-feedback-badge', {
          'horizontal-layout': layoutIsHorizontal,
        })}
      >
        <span className={classNames(className, 'material-icons')}>{iconName}</span>
      </div>
    );
  };

  const eyeIcon = () => {
    if (!bestEditTypes.includes(slideSubType) || !hasAnswerBase) {
      return null;
    }
    return (
      <div
        className={classNames('EyeIcon', {
          'horizontal-layout': layoutIsHorizontal,
          'cursor-pointer': !!activityComplete,
        })}
        onClick={(event) => eyeClickFunction(event, choiceId, choiceDisabled)}
      >
        <span
          className={classNames({
            'material-icons-outlined': showEyeIcon && displayedEditId !== choiceId,
            'material-icons': displayedEditId === choiceId,
          })}
        >
          visibility
        </span>
      </div>
    );
  };

  const renderCaret = () => {
    if (
      slideSubType !== CataTypes.EditVertical ||
      (!hasAnswerBase && slideSubType === CataTypes.EditVertical)
    ) {
      return null;
    }
    return (
      <div
        className={classNames('Caret', {
          'cursor-pointer': !!activityComplete,
        })}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          caretClickFunction(choice._id || '');
        }}
      >
        <CSSTransition in={caretExpanded} timeout={300} classNames="caret-expand">
          <div className="Caret-animation">
            <span className="material-icons">keyboard_arrow_right</span>
          </div>
        </CSSTransition>
      </div>
    );
  };

  const renderEditSetPreview = () => {
    const height = caretExpanded ? 'auto' : 0;

    return (
      <AnimateHeight
        id="panel"
        className="CATAEditSetPreview-animation"
        duration={300}
        height={height}
      >
        <div className="CATAEditSetPreview">{htmlParser(cleanContent(editSetPreview))}</div>
      </AnimateHeight>
    );
  };

  const mouseEnter = ({ e, choiceDisabled, isFeedbackButton }: IMouseEnterProps) => {
    const isShowExplanationButton = e.currentTarget.classList.contains(
      'CATAChoice-show-explanation-button',
    );

    // If option button, show eye icon only if choice is not disabled and activity is not complete
    // If explanation button, show eye icon only if choice is not disabled
    if (choiceDisabled || (activityComplete && !isShowExplanationButton)) {
      return;
    }

    // Don't show eye icon if the choice is already selected and is not a feedback button
    if (isSelected && !isFeedbackButton) {
      return;
    }

    setShowEyeIcon(true);
  };

  const mouseLeave = () => {
    if (displayedEditId === choiceId) {
      return;
    }

    setShowEyeIcon(false);
  };

  // Styles for animating gaps when show/hide explanation panels
  const defaultStyle = {
    transition: `gap ${300}ms`,
    gap: '0px',
  };
  const transitionStyles: { [id: string]: React.CSSProperties } = {
    entering: { gap: '12px' },
    entered: { gap: '12px' },
    exiting: { gap: '0px' },
    exited: { gap: '0px' },
  };

  // Styles for animating gaps when show/hide edit set preview
  const defaultChoicePanelStyle = {
    transition: `gap ${300}ms`,
    gap: '0px',
  };

  const transitionChoicePanelStyles: { [id: string]: React.CSSProperties } = {
    entering: { gap: '10px' },
    entered: { gap: '10px' },
    exiting: { gap: '0px' },
    exited: { gap: '0px' },
  };

  const hasNudgeBanner = activityComplete && ((isSelected && !correct) || (!isSelected && correct));
  const supplementalFullyOpen =
    supplementalDocState &&
    supplementalDocState === SupplementalDocStates.FullyOpen &&
    !activityComplete &&
    windowSize !== WindowSizes.Large;

  const choiceDisabled = supplementalFullyOpen || isSubmitting;
  return (
    // Animate gap between choice panel and feedback/explanation panel.
    <Transition in={explanationExpanded} timeout={300}>
      {(state) => (
        <OverlayTrigger
          overlay={
            <Popover
              id="disabled-choice-popover"
              className={classNames({ 'd-none': !supplementalFullyOpen })}
            >
              <Popover.Content>
                {supplementalFullyOpen ?? 'Unavailable when Secondary Document is fully expanded.'}
              </Popover.Content>
            </Popover>
          }
        >
          <button
            style={{
              ...defaultStyle,
              ...transitionStyles[state],
            }}
            key={choiceId}
            className={classNames('CATAChoice', {
              'should-pulse': choicesShouldPulse,
              edit: isBestEdit,
              'horizontal-layout': layoutIsHorizontal,
              'extra-margin':
                activityComplete &&
                slideSubType !== CataTypes.EditHorizontal &&
                ((isSelected && !correct) || (!isSelected && correct)),
              disabled: choiceDisabled,
            })}
            aria-label={choiceText}
            aria-pressed={isSelected}
            data-id={choiceId}
            onMouseEnter={(e) =>
              mouseEnter({ e, choiceDisabled: !!choiceDisabled, isFeedbackButton: false })
            }
            onMouseLeave={mouseLeave}
            onClick={(event) => {
              if (choiceDisabled) {
                return;
              }

              choiceClickFunction(event, setShowEyeIcon);
            }}
          >
            {/* Animate gap between main choice text and edit set preview */}
            <Transition in={caretExpanded} timeout={300}>
              {(state) => (
                <div
                  style={{
                    ...defaultChoicePanelStyle,
                    ...transitionChoicePanelStyles[state],
                  }}
                  className={classNames('CATAChoice-panel', {
                    selected: isSelected,
                    grayedOut: !isSelected && activityComplete,
                    edit: isBestEdit,
                    'horizontal-layout': layoutIsHorizontal,
                    'cursor-pointer': !activityComplete,
                    'has-nudge-banner': hasNudgeBanner,
                  })}
                  aria-hidden={true}
                >
                  {/* Eye Icon */}
                  {showEyeIcon && eyeIcon()}

                  <div
                    className={classNames('CATAChoice-main-content', {
                      'horizontal-layout': layoutIsHorizontal,
                    })}
                  >
                    {
                      // Commented out code to remove the dropdown (caret) in CATA slides.
                      // The dropdown preview for document edits is redundant and may confuse users.
                      // Moving forward, document edits should only be visible within the document itself.
                    }

                    {/* Show/Hide Edit Set preview Arrow */}
                    {/* {!hidePreviewCaret && renderCaret()} */}

                    {/* Choice Text */}
                    <div
                      className={classNames('CATAChoice-text', {
                        'horizontal-layout': layoutIsHorizontal,
                      })}
                    >
                      {htmlParser(choiceText)}
                    </div>

                    {/* Explanation Button */}
                    {activityComplete && (
                      <div
                        className={classNames('explanation-button-wrapper', {
                          horizontal: layoutIsHorizontal,
                        })}
                      >
                        {explanationButton()}
                      </div>
                    )}
                  </div>

                  {/* Edit Set Preview */}
                  {renderEditSetPreview()}

                  {/* Quiz Complete Feedback Content */}
                  {activityComplete && (
                    <>
                      <CATAChoiceNudgeBanner
                        hasInteractiveDocument={hasInteractiveDocument}
                        layoutIsHorizontal={layoutIsHorizontal}
                        isSelected={isSelected}
                        correct={correct}
                        windowSize={windowSize}
                        slideMainType={slideMainType}
                        slideSubType={slideSubType}
                      />
                      {feedbackBadge()}
                    </>
                  )}
                </div>
              )}
            </Transition>

            {activityComplete && !layoutIsHorizontal && (
              <CATAChoiceExplanationPanel
                explanation={explanation}
                showExplanation={explanationExpanded}
              />
            )}
          </button>
        </OverlayTrigger>
      )}
    </Transition>
  );
};

export default CATAChoice;
